<template>
  <div class="img-root">
    <side-preview
      class="d-flex"
      v-if="sidePreview"
      @click.native="$refs.file.click()"
      @drop="dropFile"
    >
      <upload-card />

      <img :src="img" class="img-img" />
    </side-preview>

    <full-preview v-else-if="fullPreview" @click.native="$refs.file.click()" @drop="dropFile">
      <upload-card v-if="!img" />
      <img v-else :src="img" class="img-img" />
    </full-preview>

    <multi-preview v-else-if="multiPreview">
      <div @drop.prevent="dropFile" @dragover.prevent @click="$refs.file.click()">
        <upload-card v-if="isEdit" />
      </div>

      <div class="d-flex flex-wrap">
        <div v-for="img in imgs" style="position: relative" :key="img.id">
          <span v-if="isEdit" class="img-img-remove" @click="()=>removeImg(img)">&times;</span>
          <img :src="img.image_url" class="img-img-multi ml-1 mt-1" />
        </div>
      </div>
    </multi-preview>

    <v-dialog v-model="cropperDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">{{ $t("crop_img") }}</v-card-title>

        <v-card-text>
          <vue-cropper
            :key="cropperKey"
            ref="cropper"
            alt="Crop img"
            v-if="imgToCrop"
            :src="imgToCrop"
            :cropmove="cropImage"
            :aspect-ratio="1"
            style="height: 300px"
          ></vue-cropper>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" text @click="dialog = false">{{ $t("cancel") }}</v-btn>
          <v-btn
            color="green darken-1"
            text
            @click="saveCrop"
            :disabled="!this.crop"
          >{{ $t("save") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <input type="file" style="display: none" :accept="allowedExt" ref="file" @change="inputHandler" />
  </div>
</template>

<script>
import VueCropper from "vue-cropperjs";
import SidePreview from "@/components/shared/ImgHandler/SidePreview.vue";
import FullPreview from "@/components/shared/ImgHandler/FullPreview.vue";
import MultiPreview from "@/components/shared/ImgHandler/MultiPreview.vue";
import UploadCard from "@/components/shared/ImgHandler/UploadCard.vue";
// eslint-disable-next-line import/no-extraneous-dependencies
import "cropperjs/dist/cropper.css";

const allowedExt = ["image/png", "image/jpg", "image/jpeg"];
const maxRawSize = 1e7;
const maxSize = 2e6;

export default {
  components: {
    VueCropper,
    SidePreview,
    FullPreview,
    UploadCard,
    MultiPreview
  },
  props: {
    sidePreview: {
      type: Boolean,
      default: null
    },
    fullPreview: {
      type: Boolean,
      default: null
    },
    multiPreview: {
      type: Boolean,
      default: null
    },
    defaultValue: {
      type: Array,
      default: null
    },
    disableCropper: {
      type: Boolean,
      default: null
    },
    isEdit: {
      type: Boolean,
      default: null
    },
    direct: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      img: null,
      imgs: [],
      crop: null,
      imgToCrop: null,
      cropperDialog: null,
      cropperKey: 0
    };
  },

  created() {
    if (this.defaultValue) {
      if (this.multiPreview) {
        this.imgs = this.defaultValue;
      } else {
        this.img = this.defaultValue;
      }
    }
  },

  methods: {
    dropFile(e) {
      this.fileHandler(e.dataTransfer.files[0]);
    },
    inputHandler(e) {
      this.fileHandler(e.target.files[0]);
    },
    fileHandler(file) {
      if (!file) return;

      if (!allowedExt.includes(file.type)) {
        this.$emit("finish", "WRONG_FORMAT");
        return;
      }

      const that = this;
      const r = new FileReader();

      r.onload = function() {
        const img = new Image();

        img.onload = function() {
          if (!that.disableCropper && file.size > maxRawSize) {
            that.imgToCrop = r.result;
            that.cropperDialog = true;
            that.cropperKey += 1;
          } else if (that.multiPreview) {
            const image = { id: null, image_url: r.result };
            if (that.direct) {
              image.file = file;
            }
            that.imgs.push(image);
            that.$emit("finish", that.imgs);
          } else {
            that.img = r.result;
            if (that.direct) {
              that.$emit("finish", { id: null, image_url: r.result, file });
            } else {
              that.$emit("finish", r.result);
            }
          }
        };

        img.src = r.result;
      };

      r.readAsDataURL(file);
    },
    cropImage() {
      const cropped = this.$refs.cropper
        .getCroppedCanvas()
        .toDataURL("image/jpeg", 0.5);
      this.crop = cropped;
    },
    saveCrop() {
      this.cropperDialog = false;
      if (this.crop.length > maxSize) {
        this.$emit("finish", "OVERSIZE");
      } else if (this.multiPreview) {
        this.imgs.push({ id: null, image_url: this.crop });
        this.$emit("finish", this.imgs);
        this.crop = null;
      } else {
        this.img = this.crop;
        this.$emit("finish", this.img);
        this.crop = null;
      }
    },
    async removeImg(img) {
      if (!img.id) {
        this.imgs = this.imgs.filter(x => x.image_url !== img.image_url);
      } else {
        const res = await this.$http("delete", `images/${img.id}`);
        if (res.status === 200) {
          this.imgs = this.imgs.filter(x => x.id !== img.id);
        }
      }
    },
    clearImage() {
      this.img = null;
    }
  },
  computed: {
    allowedExt() {
      return allowedExt.join(", ");
    }
  }
};
</script>

<style scoped>
.img-root {
  width: 100%;
  height: 100%;
}

.img-img {
  max-width: 100%;
  max-height: 100%;
}

.img-img-side {
  max-width: 150px;
  max-height: 150px;
}

.img-img-multi {
  max-width: 100px;
  max-height: 100px;
  border: 1px solid #e6e6e6;
  border-radius: 2px;
  padding: 3px;
}

.img-img-remove {
  position: absolute;
  cursor: pointer;
  font-size: 1.5rem;
  top: 5px;
  right: 5px;
  z-index: 1;
}
</style>
